import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Icon, type IconName } from '../../components/Icon'
import { ChannelMark } from '../../components/ui'
import { PLAN_PRICE } from '../../lib/company'
import { Heading, Section, TrialCta, WebsiteStart } from './parts'

// ---------- Why speed ----------

export function SpeedWins() {
  const race = [
    { n: 'You, with Lobby', t: '8 s', w: '1.5%', you: true },
    { n: 'Salon A', t: '47 min', w: '22%' },
    { n: 'Salon B', t: 'next morning, 9:12', w: '62%' },
    { n: 'Salon C', t: 'no reply', w: '0%' },
  ]
  return (
    <Section className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-20">
      <div className="flex flex-col gap-6 lg:w-[520px] lg:shrink-0">
        <Heading eyebrow="Why speed" sans="Reply first," serif="win the client." />
        <p className="text-[17px] leading-[27px]">
          When someone messages three salons about the same treatment, the one that answers first usually gets the booking. Not the
          cheapest, not the nicest feed. The fastest.
        </p>
        <p className="text-[16px] leading-[26px] text-muted">
          Most salons reply slowly. Not because they don’t care, but because the message arrives while you are with a client, halfway
          through a set of nails, or asleep. Clients are rarely lost to a competitor. They are lost to an unanswered DM.
        </p>
      </div>
      <figure className="card flex flex-1 flex-col gap-[18px] !rounded-[22px] p-5 lg:p-8">
        <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
          <figcaption className="font-head text-[18px] font-semibold">Time to first reply</figcaption>
          <span className="text-[12px] text-muted">Same DM to four salons · example</span>
        </div>
        {race.map((r) => (
          <div key={r.n} className="flex flex-col gap-[7px]">
            <div className="flex items-center justify-between gap-3 text-[14px]">
              <span className={r.you ? 'font-semibold' : 'font-medium text-muted'}>{r.n}</span>
              <span className="flex items-center gap-2">
                {r.you && <span className="chip !py-0.5 bg-accent-soft text-accent">Booked</span>}
                <span className={`font-semibold ${r.you ? 'text-accent' : 'text-muted'}`}>{r.t}</span>
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-sand">
              <div className={`h-2.5 min-w-2 rounded-full ${r.you ? 'bg-accent' : 'bg-faint'}`} style={{ width: r.w, display: r.w === '0%' ? 'none' : undefined }} />
            </div>
          </div>
        ))}
        <p className="text-[13px] italic text-muted">By the time Salon A answers, the client has already booked.</p>
      </figure>
    </Section>
  )
}

// ---------- Where it answers ----------

function Bubble({ from, children, meta }: { from: 'lead' | 'bot'; children: ReactNode; meta?: string }) {
  return (
    <div className={`flex ${from === 'bot' ? 'justify-end' : 'justify-start'}`}>
      <p
        className={`flex max-w-[88%] flex-col gap-0.5 px-2.5 py-2 text-[12.5px] leading-[18px] ${
          from === 'bot' ? 'rounded-[10px_2px_10px_10px] bg-bubble-assistant' : 'rounded-[2px_10px_10px_10px] bg-surface'
        }`}
      >
        {meta && <span className={`text-[10.5px] font-semibold ${from === 'bot' ? 'text-accent' : 'text-muted'}`}>{meta}</span>}
        {children}
      </p>
    </div>
  )
}

export function ChannelsSection() {
  const cards: { channel: 'instagram' | 'messenger'; title: string; what: string; body: string; visual: ReactNode }[] = [
    {
      channel: 'instagram',
      title: 'Instagram',
      what: 'Direct messages and comments',
      body: 'Someone comments “Price?” under your latest nail set. Lobby sends them a private message with the price and adds a short public reply.',
      visual: (
        <>
          <p className="rounded-[10px] bg-surface px-2.5 py-2 text-[12.5px] leading-[18px]">
            <span className="font-semibold">lena.k</span> Price for this set?
          </p>
          <p className="ml-5 rounded-[10px] bg-surface px-2.5 py-2 text-[12.5px] leading-[18px] text-muted">
            <span className="font-semibold text-ink">studiomira</span> Sent you a DM with the details!
          </p>
          <span className="chip self-start bg-accent-soft text-accent">
            <Icon name="send" size={11} /> Private reply sent
          </span>
        </>
      ),
    },
    {
      channel: 'messenger',
      title: 'Facebook',
      what: 'Messenger and Page comments',
      body: 'Messages to your Page and comments on your posts get the same answers, from the same profile and rules.',
      visual: (
        <>
          <Bubble from="lead">Anything free Saturday morning for a lash lift?</Bubble>
          <Bubble from="bot" meta="Studio Mira · 4 s later">
            Saturday 10:30 is free. Is it your first lash lift with us?
          </Bubble>
        </>
      ),
    },
  ]
  return (
    <Section id="channels" className="flex flex-col gap-12">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <Heading eyebrow="Where it answers" sans="Instagram and Facebook." serif="One assistant, one inbox." />
        <p className="text-[16px] leading-[25px] text-muted lg:w-[380px]">
          Connect one or both. Every DM and comment lands in the same dashboard, with the same price list and rules behind it. WhatsApp comes later.
        </p>
      </div>
      <ul className="grid gap-5 lg:grid-cols-2">
        {cards.map((c) => (
          <li key={c.channel} className="card flex flex-col gap-5 !rounded-[20px] p-6 lg:p-7">
            <div className="flex items-center gap-3">
              <ChannelMark channel={c.channel} size={40} />
              <div className="flex flex-col">
                <h3 className="font-head text-[20px] font-semibold tracking-[-0.3px]">{c.title}</h3>
                <p className="text-[13.5px] font-medium text-accent">{c.what}</p>
              </div>
            </div>
            <p className="text-[15px] leading-[23px] text-muted">{c.body}</p>
            <div className="mt-auto flex flex-col gap-2 rounded-[14px] bg-wa p-3" aria-hidden>
              {c.visual}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}

// ---------- What happens when a customer writes ----------

export function CustomerFlow() {
  const steps: { icon: IconName; title: string; body: string; example: string; tone: 'plain' | 'wa' | 'owner' }[] = [
    {
      icon: 'message-circle',
      title: 'A client writes, on any channel',
      body: 'In your Instagram DMs, under a post or on Messenger. At 22:14, from your feed, Google or an ad.',
      example: '“Hi, any slot for gel nails this week?”',
      tone: 'plain',
    },
    {
      icon: 'sparkles',
      title: 'Lobby answers in seconds',
      body: 'It asks what you need to know, answers from your price list and hours, and offers a time or your booking link.',
      example: '8 s later: “Is it a new set or a refill?”',
      tone: 'wa',
    },
    {
      icon: 'repeat',
      title: 'It follows up',
      body: 'When the client goes quiet, it checks in once at the time you set, so the lead doesn’t go cold.',
      example: 'Next day 10:00: “Shall I keep Thursday 15:30 for you?”',
      tone: 'wa',
    },
    {
      icon: 'hand',
      title: 'You jump in from your phone',
      body: 'Reply from the Instagram app or Meta Business Suite and Lobby steps back in that chat. Type /bot to hand it back.',
      example: 'Mira took over · assistant paused',
      tone: 'owner',
    },
  ]
  return (
    <Section className="flex flex-col gap-12">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <Heading eyebrow="When a client writes" sans="It answers and follows up." serif="You jump in when you want." />
        <p className="text-[16px] leading-[25px] text-muted lg:w-[360px]">
          No new app for your clients or for you. It answers on the Instagram and Facebook accounts you already use.
        </p>
      </div>
      <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <li key={s.title} className="card flex flex-col gap-[18px] !rounded-[20px] p-6">
            <div className="flex items-center justify-between">
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-[12px] ${s.tone === 'owner' ? 'bg-ink text-white' : 'bg-sand text-ink'}`}
              >
                <Icon name={s.icon} size={18} />
              </span>
              <span className="h-serif text-[34px] text-muted" aria-hidden>
                0{i + 1}
              </span>
            </div>
            <h3 className="font-head text-[20px] font-semibold tracking-[-0.4px]">{s.title}</h3>
            <p className="text-[15px] leading-[23px] text-muted">{s.body}</p>
            <p
              className={`mt-auto rounded-[10px] px-3 py-2.5 text-[13px] ${
                s.tone === 'wa' ? 'bg-bubble-assistant' : s.tone === 'owner' ? 'bg-bubble-owner font-semibold text-owner-ink' : 'border border-border'
              }`}
            >
              {s.example}
            </p>
          </li>
        ))}
      </ol>
    </Section>
  )
}

// ---------- The five jobs (SPEC.md 1, "Problems it solves") ----------

const JOBS: { title: string; industry: string; today: string; fix: string; steps: string[]; now?: boolean }[] = [
  {
    title: 'Booking requests',
    industry: 'e.g. Nail studio',
    today: '30 DMs a week asking for prices and free slots, answered between clients hours later. By then she has booked elsewhere.',
    fix: 'Replies within 60 seconds, answers from your price list, asks new or returning, offers a slot or your booking link.',
    steps: ['0:08 reply', '2 questions', 'Booked'],
    now: true,
  },
  {
    title: 'Appointment reminders',
    industry: 'e.g. Beauty salon',
    today: '20 to 30% of bookings lost to no-shows. Nobody confirms; cancellations leave empty chairs.',
    fix: 'Confirms 24 hours before, reminds the morning of, offers freed slots to people waiting.',
    steps: ['24 h before', 'Morning of', 'Rebooked'],
  },
  {
    title: 'Consultation before treatment',
    industry: 'e.g. Laser studio',
    today: 'Skin type, medication and patch test are asked at the door, so first appointments run late or get cancelled.',
    fix: 'Sends the consultation questions before the first visit and books the patch test. Anything medical goes to you.',
    steps: ['Questions', 'Patch test', 'First session'],
  },
  {
    title: 'Package follow-up',
    industry: 'e.g. Hair removal',
    today: '10 consultations a week, packages quoted, follow-up on none of them.',
    fix: 'Checks in on day 2, 5 and 10, answers questions, passes buying signals to you.',
    steps: ['Day 2', 'Day 5', 'Day 10'],
  },
  {
    title: 'Rebooking',
    industry: 'e.g. Hair salon',
    today: '400 clients who haven’t been back in three months. Nobody messages them.',
    fix: 'Writes when a refill or cut is due, then at 60 and 90 days, with a free slot.',
    steps: ['Refill due', '60 days', '90 days'],
  },
]

function JobCard({ job }: { job: (typeof JOBS)[number] }) {
  const dark = !!job.now
  return (
    <article
      className={`flex flex-col gap-4 rounded-[20px] border p-7 ${dark ? 'border-ink bg-ink text-white' : 'border-border bg-surface'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-head text-[22px] font-semibold tracking-[-0.4px]">{job.title}</h3>
        <span className={`chip ${dark ? 'bg-mint text-ink' : 'bg-sand text-muted'}`}>{job.now ? 'Available now' : 'Coming soon'}</span>
      </div>
      <div className="flex flex-col gap-1">
        <p className={`text-[11px] font-semibold uppercase tracking-[1.2px] ${dark ? 'text-white/70' : 'text-muted'}`}>
          Today · {job.industry.replace('e.g. ', '')}
        </p>
        <p className={`text-[15px] leading-[23px] ${dark ? 'text-white/80' : 'text-muted'}`}>{job.today}</p>
      </div>
      <div className="flex flex-col gap-1">
        <p className={`text-[11px] font-semibold uppercase tracking-[1.2px] ${dark ? 'text-mint' : 'text-accent'}`}>With Lobby</p>
        <p className="text-[15px] font-medium leading-[23px]">{job.fix}</p>
      </div>
      <div className={`mt-auto flex flex-wrap items-center gap-1.5 border-t pt-4 ${dark ? 'border-white/15' : 'border-border'}`}>
        {job.steps.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span
              className={`rounded-full border px-2.5 py-[5px] text-[12px] font-medium ${
                i === job.steps.length - 1
                  ? dark
                    ? 'border-transparent bg-mint text-ink'
                    : 'border-transparent bg-accent-soft text-accent'
                  : dark
                    ? 'border-white/15 bg-white/10'
                    : 'border-border bg-bg'
              }`}
            >
              {s}
            </span>
            {i < job.steps.length - 1 && <Icon name="chevron-right" size={12} className={dark ? 'text-white/50' : 'text-faint'} />}
          </span>
        ))}
      </div>
    </article>
  )
}

export function Jobs() {
  return (
    <Section id="what" className="flex flex-col gap-12">
      <div className="flex flex-col items-center gap-4 text-center">
        <Heading eyebrow="What it does" sans="Five jobs your salon means to do," serif="and never has time for." align="center" />
        <p className="max-w-[620px] text-[16px] text-muted">
          Booking requests are live today. The other four arrive as they are ready, on every channel you connect and at no extra cost.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        {JOBS.slice(0, 2).map((j) => (
          <JobCard key={j.title} job={j} />
        ))}
      </div>
      <div className="-mt-7 grid gap-5 lg:grid-cols-3">
        {JOBS.slice(2).map((j) => (
          <JobCard key={j.title} job={j} />
        ))}
      </div>
    </Section>
  )
}

// ---------- Setup: sign up, AI drafts, calibrate, connect ----------

function MiniCard({ children }: { children: ReactNode }) {
  return <div className="flex w-full max-w-[300px] flex-col gap-2.5 rounded-[14px] bg-white/95 p-4 shadow-[0_14px_36px_0_#16130F33]">{children}</div>
}

export function SetupSteps() {
  const tiles: { title: string; body: string; visual: ReactNode }[] = [
    {
      title: 'Sign up with your website',
      body: 'Enter your website and email, then your business name. No password, no sales call.',
      visual: (
        <MiniCard>
          <p className="text-[11px] font-semibold uppercase tracking-[1.2px] text-muted">Website</p>
          <p className="rounded-[8px] border border-border px-2.5 py-2 text-[12.5px]">studiomira.de</p>
          <p className="text-[11px] font-semibold uppercase tracking-[1.2px] text-muted">Business</p>
          <p className="rounded-[8px] border border-border px-2.5 py-2 text-[12.5px]">Studio Mira</p>
        </MiniCard>
      ),
    },
    {
      title: 'The AI drafts your profile',
      body: 'It reads your website: hours, treatments, prices, the questions clients ask. You confirm each line.',
      visual: (
        <MiniCard>
          {[
            ['Website', 'studiomira.de', 'Generate with AI'],
            ['Opening hours', 'Tue–Sat 9:00–19:00', 'AI draft'],
            ['Prices it may name', 'Gel manicure €35', 'AI draft'],
          ].map(([l, v, chip]) => (
            <div key={l} className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-muted">{l}</span>
              <span className="flex items-center justify-between gap-2 rounded-[8px] border border-border px-2.5 py-1.5 text-[12.5px]">
                {v}
                <span className="chip !px-1.5 !py-0.5 !text-[10.5px] bg-accent-soft text-accent">
                  <Icon name="sparkles" size={10} />
                  {chip}
                </span>
              </span>
            </div>
          ))}
        </MiniCard>
      ),
    },
    {
      title: 'Answer a few questions',
      body: 'Sample messages from clients. Pick the reply you would send, or fix it. That sets the tone and the limits.',
      visual: (
        <MiniCard>
          <p className="text-[12px] font-semibold">Client: “How much is laser for full legs?”</p>
          <p className="flex items-start gap-2 rounded-[8px] border-2 border-accent bg-accent-soft px-2.5 py-2 text-[12.5px]">
            <span className="mt-[3px] h-3 w-3 shrink-0 rounded-full border-[3px] border-accent bg-white" />
            Full legs start at €89 a session. Shall I book a free consultation and patch test?
          </p>
          <p className="flex items-start gap-2 rounded-[8px] border border-border px-2.5 py-2 text-[12.5px] text-muted">
            <span className="mt-[3px] h-3 w-3 shrink-0 rounded-full border border-faint" />
            Prices depend on your skin. Please call the salon.
          </p>
        </MiniCard>
      ),
    },
    {
      title: 'Connect your channels',
      body: 'After you start the trial: your Facebook Page and Instagram. Test from a second account, then go live.',
      visual: (
        <MiniCard>
          {(
            [
              ['messenger', 'Facebook Page'],
              ['instagram', 'Instagram'],
            ] as const
          ).map(([ch, label]) => (
            <div key={ch} className="flex items-center gap-2.5 rounded-[8px] border border-border px-2.5 py-2 text-[12.5px]">
              <ChannelMark channel={ch} size={20} />
              <span className="flex-1 font-medium">{label}</span>
              <span className="chip !px-1.5 !py-0.5 !text-[10.5px] bg-accent-soft text-accent">
                <span className="h-[6px] w-[6px] rounded-full bg-accent" /> Connected
              </span>
            </div>
          ))}
        </MiniCard>
      ),
    },
  ]
  return (
    <Section id="how" className="flex flex-col gap-12">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <Heading eyebrow="How it works" sans="Set up in 10 to 15 minutes," serif="on your own." />
        <p className="text-[16px] leading-[25px] text-muted lg:w-[360px]">
          The AI drafts, you correct. You set it up yourself and never write a prompt.
        </p>
      </div>
      <ol className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
        {tiles.map((t, i) => (
          <li key={t.title} className="flex flex-col gap-5">
            <div className="flex h-[260px] items-center justify-center rounded-[20px] bg-sand p-6" aria-hidden>
              {t.visual}
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="flex items-center gap-2.5 font-head text-[19px] font-semibold tracking-[-0.3px]">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[12px] font-bold text-white">{i + 1}</span>
                {t.title}
              </h3>
              <p className="text-[15px] leading-[23px] text-muted">{t.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-col gap-4 rounded-[20px] border border-border bg-surface p-5 lg:p-6">
        <p className="flex items-center gap-2 text-[14px] font-semibold">
          <Icon name="timer" size={16} className="text-accent" /> All steps, about 10 to 15 minutes
        </p>
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[13.5px]">
          {SETUP_ORDER.map((t, i) => (
            <li key={t} className="flex items-center gap-1.5">
              <span className={`rounded-full px-3 py-[6px] font-medium ${i === SETUP_ORDER.length - 1 ? 'bg-accent text-white' : 'bg-sand'}`}>
                <span className={i === SETUP_ORDER.length - 1 ? 'text-white/80' : 'text-muted'}>{i + 1}.</span> {t}
              </span>
              {i < SETUP_ORDER.length - 1 && <Icon name="chevron-right" size={12} className="text-faint" />}
            </li>
          ))}
        </ol>
      </div>
    </Section>
  )
}

const SETUP_ORDER = [
  'Sign up with your website',
  'AI drafts your profile',
  'Answer a few questions',
  'Start the free trial',
  'Connect Facebook & Instagram',
  'Test from a second account',
  'Go live',
]

// ---------- Dashboard band ----------

export function DashboardSection({ children }: { children: ReactNode }) {
  const captions = [
    ['Today', 'Who needs a person, what came in, how fast you replied.'],
    ['Conversations', 'Every chat as it happens, from every channel. Jump in from the dashboard or your phone.'],
    ['Business profile and training', 'What the assistant knows and may say. Change a line and the next reply uses it.'],
  ]
  return (
    <section className="flex flex-col gap-7 pt-[72px] lg:gap-10 lg:pt-[110px]">
      <div className="px-5">
        <Heading eyebrow="The dashboard" sans="See what it knows," serif="and what it said." align="center" />
      </div>
      <div
        className="flex justify-center bg-cover bg-center px-4 py-7 lg:h-[680px] lg:overflow-hidden lg:px-[120px] lg:pb-0 lg:pt-14"
        style={{ backgroundImage: 'url(/hero-bg.jpg)' }}
      >
        <div className="w-full max-w-[1200px] lg:h-[660px]">{children}</div>
      </div>
      <div className="mx-auto grid w-full max-w-[1440px] gap-4 px-5 sm:px-10 lg:grid-cols-3 lg:gap-5 lg:px-[120px]">
        {captions.map(([t, d]) => (
          <div key={t} className="flex flex-col gap-1.5">
            <h3 className="font-head text-[17px] font-semibold">{t}</h3>
            <p className="text-[14.5px] leading-[22px] text-muted">{d}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ---------- You stay in charge ----------

export function ControlSection() {
  const points: { icon: IconName; t: string; d: string }[] = [
    {
      icon: 'hand',
      t: 'Take over any chat',
      d: 'Reply from the Instagram app or Meta Business Suite and the assistant goes quiet in that chat. Type /bot to hand it back.',
    },
    { icon: 'shield-check', t: 'It says it’s an assistant', d: 'Every first message says so. It never pretends to be a person.' },
    {
      icon: 'message-square-off',
      t: 'Short and to the point',
      d: 'At most five replies per conversation, then a person continues. Never in group chats.',
    },
    {
      icon: 'lock',
      t: 'Your accounts, your data',
      d: 'Runs on your own Facebook and Instagram accounts, on Cloudflare in the EU. It can’t act outside the chat.',
    },
  ]
  const thread: { who: 'lead' | 'bot' | 'you' | 'sys'; meta?: string; text: string }[] = [
    { who: 'lead', meta: 'SOFIA', text: 'Can we move Thursday’s lash lift? Also, is it ok if I have a bit of sunburn?' },
    {
      who: 'bot',
      meta: 'ASSISTANT',
      text: 'Of course. Next week has Tuesday 10:00 or Wednesday 15:30. About the sunburn, let me check with Mira.',
    },
    { who: 'sys', text: 'Mira took over · assistant paused' },
    { who: 'you', meta: 'MIRA, FROM HER PHONE', text: 'Hi Sofia, Mira here. Better to wait until it has healed, so Wednesday 15:30 is perfect.' },
    { who: 'lead', meta: 'SOFIA', text: 'Perfect, thanks!' },
  ]
  return (
    <Section>
      <div className="flex flex-col gap-8 rounded-[22px] bg-ink p-6 lg:flex-row lg:items-center lg:gap-16 lg:rounded-[28px] lg:p-16">
        <div className="flex flex-1 flex-col gap-7">
          <Heading eyebrow="You stay in charge" sans="Type in the chat." serif="It steps back." dark />
          <ul className="flex flex-col gap-[18px]">
            {points.map((p) => (
              <li key={p.t} className="flex items-start gap-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-white/10 text-white">
                  <Icon name={p.icon} size={16} />
                </span>
                <div className="flex flex-col gap-[3px]">
                  <p className="font-head text-[17px] font-semibold text-white">{p.t}</p>
                  <p className="text-[14.5px] leading-[22px] text-white/70">{p.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <figure className="w-full overflow-hidden rounded-[22px] bg-wa lg:w-[400px] lg:shrink-0" aria-label="Example of an owner taking over a chat">
          <div className="flex items-center gap-2.5 bg-surface px-3.5 py-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sand text-[12px] font-bold text-muted">S</span>
            <span className="text-[13px] font-semibold">Sofia K.</span>
          </div>
          <div className="flex flex-col gap-2 px-3 py-3.5">
            {thread.map((m, i) =>
              m.who === 'sys' ? (
                <p key={i} className="self-center rounded-[8px] bg-bubble-owner px-2.5 py-[5px] text-[11px] font-semibold text-owner-ink">
                  {m.text}
                </p>
              ) : (
                <div
                  key={i}
                  className={`flex max-w-[85%] flex-col gap-1 px-2.5 py-2 ${
                    m.who === 'lead'
                      ? 'self-start rounded-[2px_10px_10px_10px] bg-surface'
                      : m.who === 'bot'
                        ? 'self-end rounded-[10px_2px_10px_10px] bg-bubble-assistant'
                        : 'self-end rounded-[10px_2px_10px_10px] bg-bubble-owner'
                  }`}
                >
                  <span
                    className={`text-[10.5px] font-semibold ${m.who === 'bot' ? 'text-accent' : m.who === 'you' ? 'text-owner-ink' : 'text-muted'}`}
                  >
                    {m.meta}
                  </span>
                  <span className="text-[12.5px] leading-[18px]">{m.text}</span>
                </div>
              ),
            )}
          </div>
        </figure>
      </div>
    </Section>
  )
}

// ---------- Learning loop (SPEC.md 4.4) ----------

export function LearningSection() {
  const steps: { icon: IconName; t: string; d: string }[] = [
    { icon: 'hand', t: 'You correct it', d: 'You take over a chat or rate a reply. Lobby notes what you did differently.' },
    { icon: 'lightbulb', t: 'It suggests a rule', d: 'Once a week it turns your corrections into short rules, each with the chats it learned from.' },
    { icon: 'circle-check', t: 'You approve, it follows', d: 'Accept a rule and every reply from then on follows it. Reject it and nothing changes.' },
  ]
  return (
    <Section className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-20">
      <div className="flex flex-col gap-6 lg:w-[460px] lg:shrink-0">
        <Heading eyebrow="Learns from you" sans="Gets better" serif="every week." />
        <p className="text-[17px] leading-[27px]">
          Your assistant learns from the way you run your business. Every chat you take over and every reply you rate shows it what works for
          your clients. Each week it suggests what to change, and you decide.
        </p>
      </div>
      <ol className="flex flex-1 flex-col gap-4">
        {steps.map((s, i) => (
          <li key={s.t} className="card flex items-start gap-4 p-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-accent-soft text-accent">
              <Icon name={s.icon} size={17} />
            </span>
            <div className="flex flex-col gap-1">
              <p className="font-head text-[17px] font-semibold">
                <span className="text-muted">{i + 1}. </span>
                {s.t}
              </p>
              <p className="text-[14.5px] leading-[22px] text-muted">{s.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  )
}

// ---------- Pricing (SPEC.md 8.8) ----------

export function Pricing() {
  const includes = [
    'One AI assistant for Instagram and Facebook',
    'DMs and comments, in one inbox',
    'Answers 24/7, including nights and weekends',
    'Up to 2,000 new conversations a month',
    'Booking requests now; reminders, consultations, package follow-up and rebooking as they ship',
    'Runs on Cloudflare in the EU',
    'Set up yourself in under 15 minutes',
  ]
  return (
    <Section id="pricing" className="flex flex-col items-center gap-12">
      <div className="flex flex-col items-center gap-4 text-center">
        <Heading eyebrow="Pricing" sans="One price." serif="Everything included." align="center" />
        <p className="text-[16px] text-muted">Excluding VAT. Try it free for 7 days on your own accounts.</p>
      </div>
      <div className="flex w-full max-w-[560px] flex-col gap-5 rounded-[22px] border border-ink bg-ink px-6 py-8 text-white sm:px-8 sm:py-10">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-[20px] font-semibold">Lobby</h3>
          <span className="chip bg-mint text-ink">One plan</span>
        </div>
        <p className="flex items-end gap-2">
          <span className="font-head text-[40px] font-semibold leading-none tracking-[-1px]">{PLAN_PRICE}</span>
          <span className="pb-1 text-[13px] text-white/70">per month · cancel any month</span>
        </p>
        <p className="text-[14.5px] leading-[22px] text-white/80">
          7 days free. We ask for a card when you sign up and charge the first {PLAN_PRICE} on day 8. Cancel before then and you pay nothing. No setup
          fee, and Lobby charges nothing per message.
        </p>
        <ul className="flex flex-col gap-2.5 border-t border-white/15 pt-[18px]">
          {includes.map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-[14px]">
              <Icon name="check" size={15} className="mt-[2px] shrink-0 text-mint" />
              {t}
            </li>
          ))}
        </ul>
        <p className="flex items-start gap-2.5 rounded-[12px] bg-white/[0.08] px-3.5 py-3 text-[13px] leading-[20px] text-white/80">
          <Icon name="circle-help" size={15} className="mt-[2px] shrink-0 text-mint" />
          Meta charges nothing for Instagram and Messenger messages. The plan price is all you pay.
        </p>
        <TrialCta label="Start 7 days free" />
      </div>
      <p className="flex w-full max-w-[560px] items-start gap-3.5 rounded-[16px] bg-accent-soft px-[22px] py-[18px] text-[15px] font-medium text-accent">
        <Icon name="timer" size={18} className="mt-[2px] shrink-0" />
        Our promise: if the median first reply is slower than 60 seconds in a calendar month, that month is free.
      </p>
    </Section>
  )
}

// ---------- FAQ ----------

const FAQ: [string, ReactNode][] = [
  [
    'Do my clients know they are talking to an AI?',
    'Yes. The first message says it is your automated assistant. Most people care more that they got an answer at 22:14.',
  ],
  [
    'Which channels does it work on?',
    'Instagram and Facebook. On Instagram it answers DMs and comments on your posts. On Facebook it answers Messenger and comments on your Page. Connect one or both; every chat lands in the same inbox. WhatsApp comes later.',
  ],
  [
    'Does Meta charge anything?',
    `No. Messages on Instagram and Messenger cost nothing extra. The ${PLAN_PRICE} for Lobby is all you pay.`,
  ],
  [
    'Can I still reply myself?',
    'Any time. Reply from the Instagram app, Meta Business Suite or the Lobby dashboard, and the assistant goes quiet in that chat. Type /bot in the chat to hand it back. It also hands over by itself when someone asks for a person, complains or describes an emergency.',
  ],
  [
    'What about comments?',
    'When someone comments on one of your Facebook or Instagram posts, Lobby answers with a private message to that person and, if you want, a short public reply under the comment. Public replies never include prices or personal details. Spam and abuse get no reply. You can turn comment replies off on the Channels page.',
  ],
  [
    'What if it says something wrong?',
    'It only uses the profile lines you confirmed and the rules you approved. When it doesn’t know, it says a person will get back to them and emails you.',
  ],
  [
    'How do I know it won’t make things up?',
    'Every reply is checked before it goes out. It may only name prices from your confirmed price list and links you approved; anything else is stopped and a person takes over. In the dashboard you can open any chat and see why it said what it said: which of your details and rules it used, and which checks the reply passed.',
  ],
  [
    'Can Meta block my account?',
    'Lobby uses Meta’s official Messenger and Instagram platforms. It answers people who wrote to you first, never writes in groups and stops after five replies, which keeps your accounts within Meta’s rules for business messaging.',
  ],
  [
    'Where is my data?',
    <>
      Lobby runs on Cloudflare in the EU. Each business’s data is kept in its own isolated database, with encrypted backups in the EU. Messages are
      deleted after 12 months. We process your customers’ data on your behalf under our{' '}
      <Link to="/dpa" className="font-medium text-ink underline underline-offset-2">
        data processing agreement
      </Link>
      .
    </>,
  ],
  ['Which languages?', 'German and English, and others you list in your profile. It replies in the language the client writes in.'],
  [
    'What does the trial cost?',
    `Nothing for 7 days. We take a card at signup and charge the first ${PLAN_PRICE} on day 8. Cancel before then in the billing portal and nothing is charged.`,
  ],
]

export function Faq() {
  return (
    <Section id="faq" className="flex flex-col gap-7 lg:flex-row lg:gap-20">
      <div className="lg:w-[380px] lg:shrink-0">
        <Heading eyebrow="FAQ" sans="Fair questions," serif="straight answers." />
      </div>
      <div className="flex-1 border-t border-border">
        {FAQ.map(([q, a], i) => (
          <details key={q} className="group border-b border-border py-[22px]" open={i === 0}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-md font-head text-[17px] font-semibold lg:text-[18px] [&::-webkit-details-marker]:hidden">
              {q}
              <Icon name="plus" size={18} className="shrink-0 text-muted group-open:hidden" />
              <Icon name="minus" size={18} className="hidden shrink-0 text-muted group-open:block" />
            </summary>
            <p className="pt-2 text-[15px] leading-[23px] text-muted">{a}</p>
          </details>
        ))}
      </div>
    </Section>
  )
}

// ---------- Final call to action ----------

export function FinalCta() {
  return (
    <section className="mx-auto w-full max-w-[1440px] px-5 py-[72px] sm:px-10 lg:px-[120px] lg:py-[110px]">
      <div
        className="relative flex min-h-[460px] flex-col items-center justify-center gap-6 overflow-hidden rounded-[22px] bg-cover bg-center p-6 text-center lg:p-14"
        style={{ backgroundImage: 'url(/hero-bg-2.jpg)' }}
      >
        <div className="absolute inset-0 bg-ink/75" aria-hidden />
        <h2 className="relative flex flex-col text-white">
          <span className="h-sans text-[38px] leading-[40px] lg:text-[60px] lg:leading-[63px]">Don’t lose clients</span>
          <span className="h-serif text-[42px] leading-[44px] lg:text-[66px] lg:leading-[69px]">to inaction.</span>
        </h2>
        <p className="relative max-w-[560px] text-[17px] text-white/85">
          Set up your assistant in under 15 minutes. 7 days free, cancel any time before day 8.
        </p>
        <WebsiteStart dark className="relative" />
        <a href="#pricing" className="relative text-[14px] font-semibold text-white/85 underline underline-offset-4 hover:text-white">
          See pricing
        </a>
      </div>
    </section>
  )
}
